import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import dayjs from 'dayjs';
import relativeTime from "dayjs/plugin/relativeTime"; 
dayjs.extend(relativeTime)

import { ChatService } from './chat.service';
import { UserTinyService } from './user-tiny/user-tiny.service';
import { LayoutService } from '../layout.service';
import { LoaderService } from '../loader.service';
import { HomeService } from '../home/home.service';
import { AuthService } from '../auth.service';
import { WebsocketService } from './websocket.service';

import { UserTinyComponent } from './user-tiny/user-tiny.component';
import { LoaderComponent } from '../shared/loader/loader.component';

import { Talk } from '../shared/interfaces/talk';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [RouterModule, UserTinyComponent, MatSidenav, MatSidenavModule, CommonModule, LoaderComponent],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('conversationsContainer') conversationsContainer!: ElementRef
  @ViewChild('conversationSidenav') conversationSidenav!: MatSidenav
  @ViewChildren('conversationDiv') conversationDivs!: QueryList<ElementRef>

  loading$ = this.loaderService.loading$
  talks: Talk[] = [];
  loading = true;
  talk!: Talk | undefined
  conversationSidenavToggled = false
  isDesktop = this.layoutService.isDesktop
  currentPage = 1
  noMoreToLoad = false
  loadingMore = false
  offset = 10
  conversationToShow$ = this.chatService.conversationToShow$
  private readonly _destroyed = new Subject<void>()
  hideSilentTalkingNotification: number | null = null
  hideDeniedSilentTalking:number | null = null
  messageSentSubscription!: Subscription

  constructor(
    private chatService: ChatService,
    private userTinyService: UserTinyService,
    private layoutService: LayoutService,
    private loaderService: LoaderService,
    private renderer: Renderer2,
    private homeService: HomeService,
    private authService: AuthService,
    private ws: WebsocketService
  ) {}

  ngOnInit() {
    this.chatService.getConversations(this.currentPage,this.offset).pipe(takeUntil(this._destroyed)).subscribe((res) => {
      this.talks = res
      this.loading = false
      
      // only keep conversations with at least one message
      const talkWithMessages = this.talks.filter(t => t.last_message !== null)
      talkWithMessages.map((t) => {
        const text =
          t.last_message && t.last_message.content
            ? decodeURIComponent(t.last_message.content).slice(0, 18) + '...'
            : null

        t.myMessage = t.last_message ? t.last_message.user_id !== t.user.id : false
        t.userDatas = {
          user: {
            firstname: t.user.firstname,
            type: 'friend',
            id: t.user.id,
            sex: t.user.sex,
            photo: t.user.photo,
          },
          message: text,
          photos: t.last_message ? !!t.last_message.photos : false,
          time: t.last_message ? t.last_message.created_at : null,
          talking: t.id,
        };
      });

      this.talks = talkWithMessages
      
      // add a new conversation to the conversation list if a message is sent and the conversation is not already in the list
      this.messageSentSubscription = this.chatService.messageSent.pipe(takeUntil(this._destroyed)).subscribe(data => {
        // check if this talk is in the conversation list
        if(!data) return
        const talk = this.talks.find(t => t.id === data.talkingId)
        const now = Math.floor(Date.now() / 1000) * 1000

        if(!talk && data.talkingId){
          this.authService.getProfile(this.chatService.relatedUserId.value!).subscribe(res => {
            const user = res
            const me = this.authService.authMe$.value
            //console.log(me)
            
            const newTalk: Talk = {
              id: data.talkingId,
              readed: false,
              new_messages: 0,
              user,
              last_message: {
                id: 0,
                content: data.message,
                user_id: me?.id || 0,
                created_at: now,
              },
              silent: 0,
              userDatas: this.chatService.relatedUser.value!,
              myMessage: true
            }

            this.talks.splice(0,0,newTalk)
          })
        } else {
          // case where the conversation already exists. 
          // The last message needs to be updated and the conversation needs to go up to first position in the chat list
          if(!talk) return
          talk.last_message.content = data.message
          talk.last_message.created_at = now

          const time = dayjs().to(dayjs(talk.last_message.created_at))
          let dateStr = ''
          let timeStr = ''
          
          if(time.includes('hour') || time.includes('minute') || time.includes('second')){
            if(time.includes('hour')){
              // get the number of hours to define if time was today or yesterday
              const timeDetails = time.split(" ");
              const hours = parseInt(timeDetails[0])
              const now = dayjs()

              if(hours < 24 && hours > now.hour()){
                dateStr = 'Yesterday'
              } else {
                dateStr = 'Today'
              }
            } else {
              dateStr = 'Today'
            }
          } else if(time === 'a day ago') {
            dateStr = 'Yesterday'
          } else {
            dateStr = dayjs(talk.last_message.created_at).format('YYYY-MM-DD')
          }

          timeStr = dayjs(talk.last_message.created_at).format('hh:mm A')

          talk.last_message.date_expression = dateStr
          talk.last_message.time_expression = timeStr

          const index = this.talks.findIndex(t => t.id === data.talkingId)
          this.talks.splice(index,1)
          this.talks.unshift(talk)
        }
      })

      this.talks.reverse()

      this.chatService.hideSilentTalkingNotification.subscribe(res => this.hideSilentTalkingNotification = res)
      this.chatService.hideDeniedSilentTalking.subscribe(res => this.hideDeniedSilentTalking = res)

      if(this.isDesktop()) {
        this.conversationSidenav.open()
        this.chatService.toggleSidenav.next(true)
      }

      this.chatService.toggleSidenav.pipe(takeUntil(this._destroyed)).subscribe((res: boolean) => {
        this.conversationSidenavToggled = res

        if(this.conversationSidenav) {
          if(res) this.conversationSidenav.open()
          else this.conversationSidenav.close()
        }
      })

      this.chatService.conversationToShow$.pipe(takeUntil(this._destroyed)).subscribe((res) => {
        this.loading = !!res
        if(res) this.conversationSidenav.open()
      });
    });
    
    this.userTinyService.success.pipe(takeUntil(this._destroyed)).subscribe((res) => {
      if(res.type === 'accept') {
        const silentTalking = this.talks.find((t) => t.user.id === res.user)

        if(!silentTalking) return
        //silentTalking.warning_silent_talking = false
      }

      if(res.type === 'delete') {
        this.talks = this.talks.filter((t) => t.user.id !== res.user)
      }
    });

    this.chatService.relationshipDeleted.pipe(takeUntil(this._destroyed)).subscribe(res => {
      if(res && this.messageSentSubscription) this.messageSentSubscription.unsubscribe()
    })

    this.chatService.conversationClosed.pipe(takeUntil(this._destroyed)).subscribe(res => {
      // remove conversation from the list
      const tempTalks = this.talks.filter(t => t.id.toString() !== res)
      this.talks = tempTalks
    })
  }

  ngAfterViewInit(){
    this.renderer.setAttribute(this.conversationsContainer.nativeElement, 'scrollTop', `-${this.conversationsContainer.nativeElement.scrollHeight}px`)
  }

  onScroll(e: Event){
    const scrollEvent = e.target as HTMLDivElement
    const scrollValue = (scrollEvent).scrollTop;
    const bottom = scrollValue > scrollEvent.scrollHeight - this.conversationsContainer.nativeElement.getBoundingClientRect().height;

    if(this.talks.length < this.offset) return

    if(bottom && !this.loadingMore && !this.noMoreToLoad){
      this.loadingMore = true
      this.currentPage++
      this.chatService.getConversations(this.currentPage,this.offset).pipe(takeUntil(this._destroyed)).subscribe(res => {
        this.talks = this.talks.concat(res.reverse())
      
        if(res.length < this.offset) this.noMoreToLoad = true

        this.loaderService.hide()

        if(!this.loadingMore) {
          window.scrollTo(0, 0)
        }

        this.loadingMore = false

        // only keep conversations with at least one message
        const talkWithMessages = this.talks.filter(t => t.last_message !== null)
        talkWithMessages.map((t) => {
          const text =
            t.last_message && t.last_message.content
              ? decodeURIComponent(t.last_message.content).slice(0, 18) + '...'
              : null

          t.myMessage = t.last_message ? t.last_message.user_id !== t.user.id : false
          t.userDatas = {
            user: {
              firstname: t.user.firstname,
              type: 'friend',
              id: t.user.id,
              sex: t.user.sex,
              photo: t.user.photo,
            },
            message: text,
            photos: t.last_message ? !!t.last_message.photos : false,
            time: t.last_message ? t.last_message.created_at : null,
            talking: t.id,
          };
        });

        this.talks = talkWithMessages
      })
    }
  }

  openTalk(talk: Talk){
    console.log('openTalk')
    // leave the room joined for the conversation previously opened
    if(this.chatService.roomToken) this.ws.close(this.chatService.roomToken)
    
    // send info to the chat service to stop message subscription in chat-conversation
    this.chatService.conversationClosed.next(this.chatService.roomToken)

    this.talk = talk
    this.chatService.talk = talk
    this.setAsRead(talk)
    this.chatService.toggleSidenav.next(true)
    this.chatService.relatedUser.next(this.talk.userDatas)
    this.chatService.relatedUserId.next(this.talk.userDatas.user.id)
    this.chatService.setConversationToShow(this.talk.id)

    // reset the new messages counter for this talk
    if(talk.new_messages) {
      if(this.homeService.liveNotifications$.value) {
        this.homeService.liveNotifications$.next(this.homeService.liveNotifications$.value - talk.new_messages)
      }
  
      talk.new_messages = 0
    }
  }

  setAsRead(talk: Talk): void {
    if(!talk.readed && !talk.myMessage) {
      talk.readed = true;
      this.chatService.setAsRead(talk.id, talk.last_message.id).pipe(takeUntil(this._destroyed)).subscribe();
    }
  }

  ngOnDestroy(){
    this.chatService.setConversationToShow(null)
    this.chatService.toggleSidenav.next(false)
    this._destroyed.next();
    this._destroyed.complete();    
    this.chatService.messageSent.next(null)
    this.messageSentSubscription.unsubscribe()
  }
}
